/**
 * Tagada Rx — activation card (merchant white-label).
 *
 * No vendor names. Merchants see Tagada Rx product language only;
 * clinical network wiring stays platform-side.
 */

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Stethoscope, Loader2, CheckCircle2, Clock4, CreditCard, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useToast } from '@/components/ui/use-toast';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useAccountManager } from '@/hooks/use-account-manager';
import {
  formatCents,
  useRxConfirmSetupFeePaid,
  useRxIntegrationStatus,
  useRxRequestActivation,
  type RxOperatingMode,
} from '@/lib/api/rx';

import { RxOnboardingDiagram } from './rx-onboarding-diagram';
import { RxPayoutSetup } from './rx-payout-setup';

export function RxActivationCard({ storeId }: { storeId: string }) {
  const { t } = useTranslation('rx');
  const { storeRoutes } = useStoreNavigate();
  const { toast } = useToast();
  const { isAccountManager } = useAccountManager();
  const statusQuery = useRxIntegrationStatus(storeId);
  const requestActivation = useRxRequestActivation();
  const confirmPaid = useRxConfirmSetupFeePaid();

  const [operatingMode, setOperatingMode] = React.useState<RxOperatingMode>('affiliate');

  if (statusQuery.isLoading || !statusQuery.data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Stethoscope className="h-4 w-4 text-primary" />
            {t('overview.title')}
          </CardTitle>
          <CardDescription>{t('activationCard.loading')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const status = statusQuery.data;

  const handleActivate = async () => {
    try {
      await requestActivation.mutateAsync({
        storeId,
        operatingMode,
        setupFeeAmountMinor: 0,
        setupFeeCurrency: 'USD',
      });
      toast({
        title: t('activationCard.toast.appSubmitted.title'),
        description: t('activationCard.toast.appSubmitted.description'),
      });
      statusQuery.refetch();
    } catch (err) {
      toast({
        title: t('activationCard.toast.appFailed.title'),
        description: err instanceof Error ? err.message : t('activationCard.toast.unknownError'),
        variant: 'destructive',
      });
    }
  };

  const handleMarkPaid = async () => {
    try {
      await confirmPaid.mutateAsync({ storeId });
      toast({
        title: t('activationCard.toast.feeConfirmed.title'),
        description: t('activationCard.toast.feeConfirmed.description'),
      });
      statusQuery.refetch();
    } catch (err) {
      toast({
        title: t('activationCard.toast.feeFailed.title'),
        description: err instanceof Error ? err.message : t('activationCard.toast.unknownError'),
        variant: 'destructive',
      });
    }
  };

  if (status.state === 'not_requested') {
    return (
      <div className="space-y-4">
        <RxOnboardingDiagram />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Stethoscope className="h-4 w-4 text-primary" />
              {t('activationCard.notRequested.title')}
            </CardTitle>
            <CardDescription>{t('activationCard.notRequested.description')}</CardDescription>
          </CardHeader>
        </Card>
        <div className="grid gap-4 lg:grid-cols-2">
          <PlanCard
            available
            eyebrow={t('activationCard.plans.affiliate.eyebrow')}
            title={t('activationCard.plans.affiliate.title')}
            rate={t('activationCard.plans.affiliate.rate')}
            rateNote={t('activationCard.plans.affiliate.rateNote')}
            setup={t('activationCard.plans.affiliate.setup')}
            subtitle={t('activationCard.plans.affiliate.subtitle')}
            points={[
              t('activationCard.plans.affiliate.points.brand'),
              t('activationCard.plans.affiliate.points.template'),
              t('activationCard.plans.affiliate.points.compliance'),
              t('activationCard.plans.affiliate.points.share'),
              t('activationCard.plans.affiliate.points.medical'),
              t('activationCard.plans.affiliate.points.minimum'),
            ]}
            footer={t('activationCard.plans.affiliate.footer')}
            action={
              <Button
                onClick={() => {
                  setOperatingMode('affiliate');
                  void handleActivate();
                }}
                disabled={requestActivation.isPending}
                className="w-full"
              >
                {requestActivation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                {t('activationCard.plans.affiliate.cta')}
              </Button>
            }
          />
          <PlanCard
            available={false}
            eyebrow={t('activationCard.plans.standalone.eyebrow')}
            title={t('activationCard.plans.standalone.title')}
            rate={t('activationCard.plans.standalone.rate')}
            rateNote={t('activationCard.plans.standalone.rateNote')}
            setup={t('activationCard.plans.standalone.setup')}
            subtitle={t('activationCard.plans.standalone.subtitle')}
            points={[
              t('activationCard.plans.standalone.points.brand'),
              t('activationCard.plans.standalone.points.compliance'),
              t('activationCard.plans.standalone.points.share'),
              t('activationCard.plans.standalone.points.medical'),
              t('activationCard.plans.standalone.points.minimum'),
            ]}
            footer={t('activationCard.plans.standalone.footer')}
            action={
              <Button disabled variant="outline" className="w-full">
                {t('activationCard.plans.standalone.cta')}
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  if (status.state === 'pending_payment') {
    return (
      <div className="space-y-4">
        <RxOnboardingDiagram />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Clock4 className="h-4 w-4 text-amber-600" />
              {t('activationCard.pending.title')}
            </CardTitle>
            <CardDescription>{t('activationCard.pending.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
              <div>
                <dt className="text-muted-foreground">{t('activationCard.fields.setupFee')}</dt>
                <dd className="font-semibold">
                  {formatCents(
                    status.settings.setupFeeAmountMinor,
                    status.settings.setupFeeCurrency,
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('activationCard.fields.mode')}</dt>
                <dd className="font-semibold">
                  {status.settings.operatingMode === 'affiliate'
                    ? t('activationCard.mode.merchantOfRecord')
                    : t('activationCard.mode.standalone')}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t('activationCard.fields.requested')}</dt>
                <dd className="font-semibold">
                  {status.settings.setupFeeRequestedAt
                    ? new Date(status.settings.setupFeeRequestedAt).toLocaleDateString()
                    : '—'}
                </dd>
              </div>
            </dl>
            {isAccountManager ? (
              <Button onClick={handleMarkPaid} disabled={confirmPaid.isPending} variant="outline">
                {confirmPaid.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                )}
                {t('activationCard.staff.markPaid')}
              </Button>
            ) : (
              <p className="text-muted-foreground text-sm">{t('activationCard.merchant.awaitConfirm')}</p>
            )}
          </CardContent>
        </Card>
        <RxPayoutSetup />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            {t('activationCard.active.title')}
          </CardTitle>
          <CardDescription>{t('activationCard.active.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <dl className="grid grid-cols-2 gap-4 text-sm md:grid-cols-3">
            <div>
              <dt className="text-muted-foreground">{t('activationCard.fields.mode')}</dt>
              <dd className="font-semibold">
                {status.settings.operatingMode === 'affiliate'
                  ? t('activationCard.mode.merchantOfRecord')
                  : t('activationCard.mode.standalone')}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('activationCard.fields.activated')}</dt>
              <dd className="font-semibold">
                {status.settings.setupFeePaidAt
                  ? new Date(status.settings.setupFeePaidAt).toLocaleDateString()
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">{t('activationCard.fields.status')}</dt>
              <dd className="font-semibold text-emerald-700">{t('activationCard.status.ready')}</dd>
            </div>
          </dl>
          <div className="flex flex-wrap gap-2">
            <Button asChild size="sm">
              <Link to={storeRoutes.RX_STOREFRONT()}>{t('activationCard.actions.launch')}</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to={storeRoutes.RX_CASES()}>{t('activationCard.actions.cases')}</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to={storeRoutes.RX_PRODUCT_OFFERINGS()}>{t('activationCard.actions.products')}</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to={storeRoutes.RX_AFFILIATE_TERMS()}>{t('activationCard.actions.commission')}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
      <RxPayoutSetup />
    </div>
  );
}

function PlanCard({
  available,
  eyebrow,
  title,
  rate,
  rateNote,
  setup,
  subtitle,
  points,
  footer,
  action,
}: {
  available: boolean;
  eyebrow: string;
  title: string;
  rate: string;
  rateNote: string;
  setup: string;
  subtitle: string;
  points: string[];
  footer: string;
  action: React.ReactNode;
}) {
  return (
    <Card className={cn(!available && 'bg-muted/20')}>
      <CardHeader className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <Badge variant={available ? 'default' : 'secondary'}>{eyebrow}</Badge>
          <span className="text-muted-foreground text-xs font-medium">{setup}</span>
        </div>
        <div>
          <CardTitle className="text-lg">{title}</CardTitle>
          <CardDescription className="mt-1">{subtitle}</CardDescription>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className="text-3xl font-semibold tracking-tight">{rate}</span>
          <span className="text-muted-foreground text-sm">{rateNote}</span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="space-y-2">
          {points.map((point) => (
            <li key={point} className="flex items-start gap-2 text-sm">
              <Check className="text-primary mt-0.5 h-4 w-4 shrink-0" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
        <p className="text-muted-foreground text-xs leading-relaxed">{footer}</p>
        {action}
      </CardContent>
    </Card>
  );
}
